import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import EventIcon from "@mui/icons-material/Event";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LockIcon from "@mui/icons-material/Lock";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { COUNTRIES } from "../../constants/countries";
import { fetchClientDropdown } from "../../services/clientService";
import { fetchProjectTimelines, fetchProjectsDropdownByClient } from "../../services/projectService";
import {
  createPatient,
  generatePatientPid,
} from "../../services/patientService";
import { formatDate } from "../../utils/dateFormat";
import {
  clearPatientFieldError,
  formatPatientValidationMessage,
  validatePatientForm,
} from "./patientFormUtils";

const initialForm = {
  pid: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  enrollment_date: "",
  email: "",
  phone_local: "",
  address1: "",
  address2: "",
  country: "United States",
  city: "",
  state: "",
  zipcode: "",
  client_id: "",
  project_id: "",
  status: "active",
};

const modalSelectSx = { width: { xs: "100%", md: 260 } };

function addDays(dateStr, days) {
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
}

function toErrorMessage(error, fallback) {
  const raw = error?.message || "";
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === "string") return parsed.detail;
  } catch {
    // ignore
  }
  return String(raw || fallback);
}

export default function PatientCreateDialog({
  open,
  onClose,
  onCreated,
  presetClientId = "",
  presetProjectId = null,
  lockClientProject = false,
}) {
  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [clients, setClients] = useState([]);
  const [projectsByClient, setProjectsByClient] = useState([]);
  const [schedulePreview, setSchedulePreview] = useState([]);

  const selectedCountry = useMemo(
    () => COUNTRIES.find((country) => country.name === form.country),
    [form.country]
  );

  const loadProjectsForClient = async (cid) => {
    if (!cid) {
      setProjectsByClient([]);
      return;
    }
    const items = await fetchProjectsDropdownByClient(cid);
    setProjectsByClient(items || []);
  };

  const loadSchedulePreview = async (projectId, anchorDateStr) => {
    if (!projectId) {
      setSchedulePreview([]);
      return;
    }
    const timelines = await fetchProjectTimelines(projectId);
    const sorted = [...(timelines || [])].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
    const anchor =
      anchorDateStr && String(anchorDateStr).trim()
        ? String(anchorDateStr).trim().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
    let previousOptimal = anchor;
    const preview = sorted.map((row, index) => {
      const type = String(row.type || "").toLowerCase();
      const tolerance = Number(row.tolerance || 0);
      const offset = Number(row.offset || 0);
      const optimal =
        index === 0
          ? anchor
          : type === "static"
            ? row.date || previousOptimal
            : addDays(previousOptimal, offset >= 1 ? offset : 1);
      const windowOpen = addDays(optimal, -Math.max(tolerance, 0));
      const windowClose = addDays(optimal, Math.max(tolerance, 0));
      previousOptimal = optimal;
      return {
        id: row.id || row.position,
        schedule: row.position,
        type,
        tolerance,
        optimal_date: optimal,
        window_open: windowOpen,
        window_close: windowClose,
      };
    });
    setSchedulePreview(preview);
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const bootstrap = async () => {
      setErrorMessage("");
      setFieldErrors({});
      const nextForm = {
        ...initialForm,
        client_id: presetClientId || "",
        project_id: presetProjectId ? String(presetProjectId) : "",
      };
      setForm(nextForm);
      setSchedulePreview([]);

      try {
        const clientItems = await fetchClientDropdown();
        if (cancelled) return;
        setClients(clientItems || []);

        if (presetClientId) {
          await loadProjectsForClient(presetClientId);
          if (cancelled) return;
        }

        if (presetClientId && presetProjectId) {
          try {
            const pidData = await generatePatientPid({
              client_id: presetClientId,
              project_id: presetProjectId,
            });
            if (!cancelled) {
              setForm((prev) => ({ ...prev, pid: pidData.pid || "" }));
            }
          } catch {
            // pid stays empty until user retries
          }
          await loadSchedulePreview(presetProjectId, nextForm.enrollment_date);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(toErrorMessage(error, "Failed to load subject form"));
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [open, presetClientId, presetProjectId]);

  useEffect(() => {
    if (!open || !form.project_id) {
      setSchedulePreview([]);
      return;
    }
    loadSchedulePreview(form.project_id, form.enrollment_date);
  }, [open, form.project_id, form.enrollment_date]);

  const handleClientChange = async (cid) => {
    if (lockClientProject) return;
    setForm((prev) => ({ ...prev, client_id: cid, project_id: "", pid: "", enrollment_date: "" }));
    await loadProjectsForClient(cid);
    setSchedulePreview([]);
  };

  const handleProjectChange = async (projectId) => {
    if (lockClientProject) return;
    setForm((prev) => ({ ...prev, project_id: projectId, pid: "" }));
    if (!form.client_id || !projectId) return;
    try {
      const pidData = await generatePatientPid({ client_id: form.client_id, project_id: projectId });
      setForm((prev) => ({ ...prev, pid: pidData.pid || "" }));
    } catch {
      setForm((prev) => ({ ...prev, pid: "" }));
    }
  };

  const handleSave = async () => {
    const phoneFull = `${selectedCountry?.dialCode || ""}${(form.phone_local || "").trim()}`;
    const validation = validatePatientForm(form, phoneFull, { requireEnrollmentDate: true });
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setErrorMessage(formatPatientValidationMessage(validation.missingLabels));
      return;
    }
    setFieldErrors({});
    setErrorMessage("");
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim(),
        gender: form.gender,
        date_of_birth: form.date_of_birth || null,
        phone: phoneFull || null,
        address1: form.address1.trim() || null,
        address2: form.address2.trim() || null,
        country: form.country.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zipcode: form.zipcode.trim() || null,
        client_id: form.client_id,
        project_id: Number(form.project_id),
        status: form.status || "active",
        email: form.email.trim() || null,
        enrollment_date: form.enrollment_date || null,
      };
      const created = await createPatient(payload);
      onCreated?.(created);
      onClose?.();
    } catch (error) {
      setErrorMessage(toErrorMessage(error, "Failed to create subject"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose?.();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Add Subject</DialogTitle>
      <DialogContent sx={{ bgcolor: "#f5f8fb" }}>
        {errorMessage ? (
          <Typography color="error" sx={{ mb: 1.5 }}>
            {errorMessage}
          </Typography>
        ) : null}
        <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="PID (Auto)" value={form.pid} disabled />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  required
                  label="Client"
                  value={form.client_id}
                  disabled={lockClientProject}
                  error={Boolean(fieldErrors.client_id)}
                  helperText={fieldErrors.client_id}
                  onChange={(event) => {
                    handleClientChange(event.target.value);
                    clearPatientFieldError(setFieldErrors, "client_id");
                  }}
                  sx={modalSelectSx}
                >
                  {(clients || []).map((c) => (
                    <MenuItem key={c.cid} value={c.cid}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  required
                  label="Project"
                  value={form.project_id}
                  disabled={lockClientProject || !form.client_id}
                  error={Boolean(fieldErrors.project_id)}
                  helperText={fieldErrors.project_id}
                  onChange={(event) => {
                    handleProjectChange(event.target.value);
                    clearPatientFieldError(setFieldErrors, "project_id");
                  }}
                  sx={modalSelectSx}
                >
                  {(projectsByClient || []).map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  value={form.first_name}
                  error={Boolean(fieldErrors.first_name)}
                  helperText={fieldErrors.first_name}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, first_name: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "first_name");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Middle Name"
                  value={form.middle_name}
                  onChange={(e) => setForm((p) => ({ ...p, middle_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  value={form.last_name}
                  error={Boolean(fieldErrors.last_name)}
                  helperText={fieldErrors.last_name}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, last_name: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "last_name");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  required
                  label="Gender"
                  value={form.gender}
                  error={Boolean(fieldErrors.gender)}
                  helperText={fieldErrors.gender}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, gender: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "gender");
                  }}
                  sx={modalSelectSx}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Date of Birth"
                  format="MM/DD/YYYY"
                  value={form.date_of_birth ? dayjs(form.date_of_birth) : null}
                  onChange={(value) => {
                    setForm((p) => ({
                      ...p,
                      date_of_birth: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                    }));
                    clearPatientFieldError(setFieldErrors, "date_of_birth");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(fieldErrors.date_of_birth),
                      helperText: fieldErrors.date_of_birth,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Enrollment date"
                  format="MM/DD/YYYY"
                  value={form.enrollment_date ? dayjs(form.enrollment_date) : null}
                  onChange={(value) => {
                    setForm((p) => ({
                      ...p,
                      enrollment_date: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                    }));
                    clearPatientFieldError(setFieldErrors, "enrollment_date");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(fieldErrors.enrollment_date),
                      helperText:
                        fieldErrors.enrollment_date ||
                        "Required; first static visit is scheduled on this date.",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Subject Status"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  sx={modalSelectSx}
                >
                  <MenuItem value="active">Active (Auto)</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label="Email"
                  value={form.email}
                  error={Boolean(fieldErrors.email)}
                  helperText={
                    fieldErrors.email ||
                    "Must be unique among subjects; can match a client manager email if no other subject uses it."
                  }
                  onChange={(e) => {
                    setForm((p) => ({ ...p, email: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "email");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  required
                  label="Country"
                  value={form.country}
                  error={Boolean(fieldErrors.country)}
                  helperText={fieldErrors.country}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, country: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "country");
                  }}
                  sx={modalSelectSx}
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Country Code" value={selectedCountry?.dialCode || ""} disabled />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  required
                  fullWidth
                  label="Mobile Number"
                  value={form.phone_local}
                  error={Boolean(fieldErrors.phone)}
                  helperText={fieldErrors.phone}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, phone_local: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "phone");
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Address
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Address 1"
                  value={form.address1}
                  error={Boolean(fieldErrors.address1)}
                  helperText={fieldErrors.address1}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, address1: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "address1");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address 2"
                  value={form.address2}
                  onChange={(e) => setForm((p) => ({ ...p, address2: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="City"
                  value={form.city}
                  error={Boolean(fieldErrors.city)}
                  helperText={fieldErrors.city}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, city: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "city");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="State"
                  value={form.state}
                  error={Boolean(fieldErrors.state)}
                  helperText={fieldErrors.state}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, state: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "state");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="Zipcode"
                  value={form.zipcode}
                  error={Boolean(fieldErrors.zipcode)}
                  helperText={fieldErrors.zipcode}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, zipcode: e.target.value }));
                    clearPatientFieldError(setFieldErrors, "zipcode");
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ bgcolor: "#fff", border: "1px solid #dde5ec", borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Schedule Timeline Preview
            </Typography>
            {(schedulePreview || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Select a project to preview schedule windows. Visit 1 uses the enrollment date (or today until you set it).
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto", pb: 1 }}>
                <Timeline
                  sx={{
                    m: 0,
                    p: 0,
                    minWidth: "max-content",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    "& .MuiTimelineItem-root": {
                      minHeight: "auto",
                      flex: "0 0 320px",
                      px: 0,
                      alignItems: "stretch",
                      "&:before": { display: "none" },
                    },
                    "& .MuiTimelineSeparator-root": {
                      width: "100%",
                      flexDirection: "row",
                      alignItems: "center",
                      minHeight: "auto",
                      mb: 1,
                    },
                    "& .MuiTimelineConnector-root": {
                      flexGrow: 1,
                      width: "auto",
                      height: 2,
                      bgcolor: "#90a4ae",
                    },
                    "& .MuiTimelineContent-root": { py: 0, px: 0 },
                  }}
                >
                  {(schedulePreview || []).map((s, index) => {
                    const isDynamic = String(s.type || "").toLowerCase() === "dynamic";
                    return (
                      <TimelineItem key={s.id}>
                        <TimelineSeparator>
                          {index > 0 ? <TimelineConnector /> : null}
                          <TimelineDot color={isDynamic ? "secondary" : "primary"}>
                            {isDynamic ? <AutorenewIcon fontSize="small" /> : <EventIcon fontSize="small" />}
                          </TimelineDot>
                          {index < schedulePreview.length - 1 ? <TimelineConnector /> : null}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Box sx={{ border: "1px solid #dce5ed", borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              Schedule {s.schedule} ({String(s.type || "").toUpperCase()})
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                              Tolerance: {s.tolerance}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#1976d2" }}>
                                <LockOpenIcon sx={{ fontSize: 18 }} />
                                <Typography variant="body2">
                                  Window Open: {s.window_open == null ? "N/A" : formatDate(s.window_open)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#2e7d32" }}>
                                <GpsFixedIcon sx={{ fontSize: 18 }} />
                                <Typography variant="body2">
                                  Optimal Date: {s.optimal_date == null ? "N/A" : formatDate(s.optimal_date)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#ed6c02" }}>
                                <LockIcon sx={{ fontSize: 18 }} />
                                <Typography variant="body2">
                                  Window Close: {s.window_close == null ? "N/A" : formatDate(s.window_close)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Create Subject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
